import { useMemo, useState, type JSX } from 'react';
import type { CategoryDef, DashboardPayload, SeasonDef, TasteDef } from '../dashboard-types.js';
import { applyWeight, type Severity, type WeightContext } from './utils.js';
import { StatsGrid } from './StatsGrid.js';
import { CardsCategory } from './cards/CardsCategory.js';
import { CardsCategorySize } from './cards/CardsCategorySize.js';
import { CardsSizeCategory } from './cards/CardsSizeCategory.js';
import { CardsCategorySizeTaste } from './cards/CardsCategorySizeTaste.js';

/** @graph-connects none */
type TabKey = 'cat' | 'cat-size' | 'size-cat' | 'cat-size-taste';
/** @graph-connects none */
type SortKey = 'default' | 'count' | 'warn' | 'lenDesc' | 'lenAsc';
/** @graph-connects none */
type FilterKey = 'all' | 'warn';

interface CurrentViewProps {
  data: DashboardPayload;
  dimensionRanges: Record<string, [number, number]>;
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business 現在スナップショットビュー。シーズン/カテゴリ/サイズ/テイストのフィルタ・並び順・ブランドセレクト除外トグル・閾値割れフィルタを管理し、4 種類のタブビュー（カテゴリ別 / カテゴリ×サイズ / サイズ×カテゴリ / カテゴリ×サイズ×テイスト）を切り替え描画する
 * @graph-connects StatsGrid [calls] KPI 上段
 * @graph-connects CardsCategory [calls] タブ 1 描画
 * @graph-connects CardsCategorySize [calls] タブ 2 描画
 * @graph-connects CardsSizeCategory [calls] タブ 3 描画
 * @graph-connects CardsCategorySizeTaste [calls] タブ 4 描画
 */
export function CurrentView(props: CurrentViewProps): JSX.Element {
  const { data } = props;
  const allSeasonKeys = useMemo<Set<SeasonDef['key']>>(
    () => new Set(data.seasons.map((s) => s.key)),
    [data.seasons],
  );
  const [selectedSeasons, setSelectedSeasons] = useState<Set<SeasonDef['key']>>(allSeasonKeys);
  const [excludeBrandSelect, setExcludeBrandSelect] = useState(false);
  const [tab, setTab] = useState<TabKey>('cat');
  const [sort, setSort] = useState<SortKey>('default');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<number>>(
    () => new Set(data.categories.map((c) => c.id)),
  );
  const [selectedSizes, setSelectedSizes] = useState<Set<string>>(() => new Set(data.sizes));
  const [selectedTasteIds, setSelectedTasteIds] = useState<Set<TasteDef['id']>>(
    () => new Set(data.tastes.map((t) => t.id)),
  );

  const ctx: WeightContext = {
    selectedSeasons,
    excludeBrandSelect,
    ranges: props.dimensionRanges,
    dimensions: data.dimensions,
    catDim: data.catDim,
  };

  /** @graph-connects none */
  function sizeTotal(c: CategoryDef, sz: string): number {
    return applyWeight(c.sizes[sz]?.total ?? 0, c, ctx);
  }
  /** @graph-connects none */
  function sizeDelta(c: CategoryDef, sz: string): number {
    return applyWeight(c.sizes[sz]?.delta ?? 0, c, ctx);
  }
  /** @graph-connects none */
  function tasteCount(c: CategoryDef, sz: string, tid: TasteDef['id']): number {
    return applyWeight(c.sizes[sz]?.tastes?.[tid] ?? 0, c, ctx);
  }
  /** @graph-connects none */
  function categoryTotal(c: CategoryDef): number {
    return data.sizes.reduce((s, sz) => s + sizeTotal(c, sz), 0);
  }
  /** @graph-connects none */
  function categoryDelta(c: CategoryDef): number {
    return data.sizes.reduce((s, sz) => s + sizeDelta(c, sz), 0);
  }
  /** @graph-connects none */
  function categoryWarnCountSizeTaste(c: CategoryDef): number {
    let n = 0;
    for (const sz of data.sizes) {
      for (const t of data.tastes) {
        if (tasteCount(c, sz, t.id) < data.thresholds.warn) n += 1;
      }
    }
    return n;
  }
  /** @graph-connects none */
  function severityClass(v: number): Severity {
    if (v < data.thresholds.danger) return 'danger';
    if (v < data.thresholds.warn) return 'warn';
    return '';
  }

  const sortedCategories = (() => {
    const list = [...data.categories];
    if (sort === 'count') list.sort((a, b) => categoryTotal(b) - categoryTotal(a));
    else if (sort === 'warn')
      list.sort((a, b) => categoryWarnCountSizeTaste(b) - categoryWarnCountSizeTaste(a));
    else if (sort === 'lenDesc') list.sort((a, b) => (b.avgLength || 0) - (a.avgLength || 0));
    else if (sort === 'lenAsc') list.sort((a, b) => (a.avgLength || 0) - (b.avgLength || 0));
    else list.sort((a, b) => a.id - b.id);
    return list;
  })();

  /** @graph-connects none */
  function passesFilter(c: CategoryDef): boolean {
    if (!selectedCategoryIds.has(c.id)) return false;
    if (filter === 'all') return true;
    return categoryWarnCountSizeTaste(c) > 0;
  }

  // KPI
  const total = data.categories.reduce((s, c) => s + categoryTotal(c), 0);
  const totalDelta = data.categories.reduce((s, c) => s + categoryDelta(c), 0);
  const topsTotal = data.categories
    .filter((c) => data.topsCategoryIds.includes(c.id))
    .reduce((s, c) => s + categoryTotal(c), 0);
  const bottomsTotal = data.categories
    .filter((c) => data.bottomsCategoryIds.includes(c.id))
    .reduce((s, c) => s + categoryTotal(c), 0);
  const topsDelta = data.categories
    .filter((c) => data.topsCategoryIds.includes(c.id))
    .reduce((s, c) => s + categoryDelta(c), 0);
  const bottomsDelta = data.categories
    .filter((c) => data.bottomsCategoryIds.includes(c.id))
    .reduce((s, c) => s + categoryDelta(c), 0);
  const warnCount = data.categories.reduce((s, c) => s + categoryWarnCountSizeTaste(c), 0);

  /** @graph-connects none */
  function toggleSeason(key: SeasonDef['key']): void {
    if (selectedSeasons.size === data.seasons.length) {
      setSelectedSeasons(new Set([key]));
      return;
    }
    const next = new Set(selectedSeasons);
    if (next.has(key)) {
      if (next.size === 1) {
        setSelectedSeasons(new Set(data.seasons.map((s) => s.key)));
        return;
      }
      next.delete(key);
    } else {
      next.add(key);
    }
    setSelectedSeasons(next);
  }
  /** @graph-connects none */
  function toggleSeasonAll(): void {
    if (selectedSeasons.size === data.seasons.length) return;
    setSelectedSeasons(new Set(data.seasons.map((s) => s.key)));
  }
  /** @graph-connects none */
  function toggleCategoryChip(id: number): void {
    const next = new Set(selectedCategoryIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedCategoryIds(next);
  }
  /** @graph-connects none */
  function toggleSizeChip(sz: string): void {
    const next = new Set(selectedSizes);
    if (next.has(sz)) next.delete(sz);
    else next.add(sz);
    setSelectedSizes(next);
  }
  /** @graph-connects none */
  function toggleTasteChip(tid: TasteDef['id']): void {
    const next = new Set(selectedTasteIds);
    if (next.has(tid)) next.delete(tid);
    else next.add(tid);
    setSelectedTasteIds(next);
  }
  /** @graph-connects none */
  function selectAllOf(kind: 'cat' | 'size' | 'taste'): void {
    if (kind === 'cat') setSelectedCategoryIds(new Set(data.categories.map((c) => c.id)));
    if (kind === 'size') setSelectedSizes(new Set(data.sizes));
    if (kind === 'taste') setSelectedTasteIds(new Set(data.tastes.map((t) => t.id)));
  }
  /** @graph-connects none */
  function selectNoneOf(kind: 'cat' | 'size' | 'taste'): void {
    if (kind === 'cat') setSelectedCategoryIds(new Set());
    if (kind === 'size') setSelectedSizes(new Set());
    if (kind === 'taste') setSelectedTasteIds(new Set());
  }
  /** @graph-connects none */
  function handleBrandSelectToggle(): void {
    setExcludeBrandSelect((v) => !v);
  }

  return (
    <div>
      <StatsGrid
        kpis={{
          total,
          totalDelta,
          topsTotal,
          bottomsTotal,
          topsDelta,
          bottomsDelta,
          warnCount,
          categoryCount: data.categories.length,
          sizeCount: data.sizes.length,
          sizeRangeLabel: data.sizes.join('〜'),
        }}
      />
      <div className="section">
        <div className="section-head">
          <div>
            <h2 className="section-title">
              <svg
                width={16}
                height={16}
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <rect x={2} y={2} width={12} height={12} rx={1.5} />
                <path d="M2 6h12M6 2v12" />
              </svg>
              選定可能型数 (pattern_id 数)
            </h2>
            <p className="section-desc">
              3時間ごとにitem_skusのpattern_idを集計し、各粒度で型数を可視化します。閾値割れの行はハイライト表示されます。
            </p>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>
            スナップショット: {data.lastUpdated}
          </div>
        </div>

        <div className="tabs">
          {(
            [
              { k: 'cat', label: 'カテゴリ別' },
              { k: 'cat-size', label: 'カテゴリ × サイズ' },
              { k: 'size-cat', label: 'サイズ × カテゴリ' },
              { k: 'cat-size-taste', label: 'カテゴリ × サイズ × テイスト' },
            ] as const
          ).map((t) => {
            const handleClick = (): void => {
              setTab(t.k);
            };
            return (
              <button
                key={t.k}
                type="button"
                className={`tab ${tab === t.k ? 'active' : ''}`}
                onClick={handleClick}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="view-toolbar">
          <div className="filter-group">
            <span className="filter-label">シーズン (item_skus.seasons):</span>
            <button
              type="button"
              className={`chip-btn ${selectedSeasons.size === data.seasons.length ? 'active' : ''}`}
              onClick={toggleSeasonAll}
            >
              全シーズン
            </button>
            {data.seasons.map((s) => {
              const handleClick = (): void => {
                toggleSeason(s.key);
              };
              return (
                <button
                  key={s.key}
                  type="button"
                  className={`chip-btn ${selectedSeasons.has(s.key) ? 'active' : ''}`}
                  onClick={handleClick}
                >
                  {s.name}
                  <span className="season-key">{s.key}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="filter-row">
          <span className="filter-label">カテゴリ:</span>
          <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
            {data.categories.map((c) => {
              const handleClick = (): void => {
                toggleCategoryChip(c.id);
              };
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`chip-btn compact-chip ${selectedCategoryIds.has(c.id) ? 'active' : ''}`}
                  onClick={handleClick}
                >
                  {c.name} <span className="season-key">id:{c.id}</span>
                </button>
              );
            })}
          </div>
          <div className="quick-actions">
            <button
              type="button"
              onClick={() => {
                selectAllOf('cat');
              }}
            >
              全選択
            </button>
            <button
              type="button"
              onClick={() => {
                selectNoneOf('cat');
              }}
            >
              全解除
            </button>
          </div>
        </div>

        {tab !== 'cat' && (
          <div className="filter-row">
            <span className="filter-label">サイズ (ac_size):</span>
            <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
              {data.sizes.map((sz) => {
                const handleClick = (): void => {
                  toggleSizeChip(sz);
                };
                return (
                  <button
                    key={sz}
                    type="button"
                    className={`chip-btn compact-chip ${selectedSizes.has(sz) ? 'active' : ''}`}
                    onClick={handleClick}
                  >
                    {sz}
                  </button>
                );
              })}
            </div>
            <div className="quick-actions">
              <button
                type="button"
                onClick={() => {
                  selectAllOf('size');
                }}
              >
                全選択
              </button>
              <button
                type="button"
                onClick={() => {
                  selectNoneOf('size');
                }}
              >
                全解除
              </button>
            </div>
          </div>
        )}

        {tab === 'cat-size-taste' && (
          <div className="filter-row">
            <span className="filter-label">テイスト (md_taste):</span>
            <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
              {data.tastes.map((t) => {
                const handleClick = (): void => {
                  toggleTasteChip(t.id);
                };
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`chip-btn compact-chip ${selectedTasteIds.has(t.id) ? 'active' : ''}`}
                    onClick={handleClick}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
            <div className="quick-actions">
              <button
                type="button"
                onClick={() => {
                  selectAllOf('taste');
                }}
              >
                全選択
              </button>
              <button
                type="button"
                onClick={() => {
                  selectNoneOf('taste');
                }}
              >
                全解除
              </button>
            </div>
          </div>
        )}

        <div className="view-toolbar" style={{ borderTop: '1px dashed var(--border)' }}>
          <div className="filter-group">
            <span className="filter-label">並び順:</span>
            {(
              [
                { k: 'default', label: 'カテゴリID' },
                { k: 'count', label: '型数の多い順' },
                { k: 'warn', label: '閾値割れ多い順' },
                { k: 'lenDesc', label: '丈の長い順 ▼' },
                { k: 'lenAsc', label: '丈の短い順 ▲' },
              ] as const
            ).map((s) => {
              const handleClick = (): void => {
                setSort(s.k);
              };
              return (
                <button
                  key={s.k}
                  type="button"
                  className={`chip-btn ${sort === s.k ? 'active' : ''}`}
                  onClick={handleClick}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
          <div className="filter-group">
            <span className="filter-label">表示:</span>
            <button
              type="button"
              className={`chip-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => {
                setFilter('all');
              }}
            >
              全て
            </button>
            <button
              type="button"
              className={`chip-btn ${filter === 'warn' ? 'active' : ''}`}
              onClick={() => {
                setFilter('warn');
              }}
            >
              閾値割れのみ
            </button>
          </div>
          <div className="filter-group">
            <button
              type="button"
              className={`toggle-pill ${excludeBrandSelect ? 'active' : ''}`}
              onClick={handleBrandSelectToggle}
              title="brand_options_skus.can_use_for_normal_styling = 0 のものを選定可能型数から除外"
            >
              <span className="switch"></span>
              <span>ブランドセレクトアイテムを除外</span>
            </button>
          </div>
          <div className="sort-info">
            ※ 閾値: <code>warn &lt; {data.thresholds.warn}</code> /{' '}
            <code>danger &lt; {data.thresholds.danger}</code> / 丈ソート: item_skus.full_length /
            dress_length / inseam の平均
          </div>
        </div>

        <div className="legend">
          <span>
            <b>表示の見方</b>
          </span>
          <span className="legend-item">
            <span className="lg-swatch lg-healthy"></span>健全 ({data.thresholds.warn}型以上)
          </span>
          <span className="legend-item">
            <span className="lg-swatch lg-warn"></span>注意 ({data.thresholds.warn}型未満)
          </span>
          <span className="legend-item">
            <span className="lg-swatch lg-danger"></span>危険 ({data.thresholds.danger}型未満)
          </span>
          <span className="legend-item">▲▼ = 前回スナップショット比</span>
          <span className="legend-item">バー = カテゴリ内最大値に対する充足度</span>
        </div>

        <div>
          {tab === 'cat' && (
            <CardsCategory
              data={data}
              sortedCategories={sortedCategories}
              passesFilter={passesFilter}
              categoryTotal={categoryTotal}
              categoryDelta={categoryDelta}
              categoryWarnCountSizeTaste={categoryWarnCountSizeTaste}
              severityClass={severityClass}
            />
          )}
          {tab === 'cat-size' && (
            <CardsCategorySize
              data={data}
              sortedCategories={sortedCategories}
              passesFilter={passesFilter}
              selectedSizes={selectedSizes}
              categoryTotal={categoryTotal}
              categoryDelta={categoryDelta}
              sizeTotal={sizeTotal}
              sizeDelta={sizeDelta}
              severityClass={severityClass}
            />
          )}
          {tab === 'size-cat' && (
            <CardsSizeCategory
              data={data}
              sortedCategories={sortedCategories}
              passesFilter={passesFilter}
              selectedSizes={selectedSizes}
              sizeTotal={sizeTotal}
              sizeDelta={sizeDelta}
              severityClass={severityClass}
            />
          )}
          {tab === 'cat-size-taste' && (
            <CardsCategorySizeTaste
              data={data}
              sortedCategories={sortedCategories}
              passesFilter={passesFilter}
              selectedSizes={selectedSizes}
              selectedTasteIds={selectedTasteIds}
              categoryTotal={categoryTotal}
              categoryDelta={categoryDelta}
              sizeTotal={sizeTotal}
              sizeDelta={sizeDelta}
              tasteCount={tasteCount}
              categoryWarnCountSizeTaste={categoryWarnCountSizeTaste}
              severityClass={severityClass}
            />
          )}
        </div>
      </div>
    </div>
  );
}
